import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, Plus, Loader2, Building2, Users, Pencil, Archive, Eye, Settings, GitCompare } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import AssignSchoolsModal from "./AssignSchoolsModal";
import AssignOwnersModal from "./AssignOwnersModal";

const API = process.env.REACT_APP_BACKEND_URL;

const buildHeaders = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id": session?.user?.id || "admin",
  "x-user-role": session?.user?.role || "admin",
  "Content-Type": "application/json",
});

const TYPE_LABELS = {
  authorizer_portfolio: "Authorizer Portfolio", charter_network: "Charter Network",
  cmo_emo: "CMO / EMO", district_regional_operator: "District / Regional Operator",
  consultant_advisor: "Consultant / Advisor", "589_labs_internal": "589 Labs (Internal)", other: "Other",
};

const STATUS_COLOR = { active: "text-green-700 bg-green-50", inactive: "text-amber-700 bg-amber-50", archived: "text-slate-500 bg-slate-100" };

export default function PortfoliosAdminPage({ session }) {
  const headers = useMemo(() => buildHeaders(session), [session]);
  const [portfolios, setPortfolios] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", type: "authorizer_portfolio", description: "", internal_notes: "", status: "active" });
  const [saving, setSaving] = useState(false);

  const [schoolsModal, setSchoolsModal] = useState(null);
  const [ownersModal, setOwnersModal] = useState(null);
  const [benchOpen, setBenchOpen] = useState(false);
  const [bench, setBench] = useState(null);

  const openBench = async () => {
    const r = await axios.get(`${API}/api/admin/portfolios/benchmark/settings`, { headers });
    setBench(r.data); setBenchOpen(true);
  };
  const saveBench = async (patch) => {
    const r = await axios.patch(`${API}/api/admin/portfolios/benchmark/settings`, patch, { headers });
    setBench(r.data); toast.success("Benchmark settings updated");
  };

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const r = await axios.get(`${API}/api/admin/portfolios${q}`, { headers });
      setPortfolios(r.data.portfolios || []);
      setTypes(r.data.types || []);
    } catch (e) {
      toast.error("Failed to load portfolios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", type: "authorizer_portfolio", description: "", internal_notes: "", status: "active" });
    setEditOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, type: p.type, description: p.description || "", internal_notes: p.internal_notes || "", status: p.status });
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/api/admin/portfolios/${editing.id}`, form, { headers });
        toast.success("Portfolio updated");
      } else {
        await axios.post(`${API}/api/admin/portfolios`, form, { headers });
        toast.success("Portfolio created");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Owners will lose access.`)) return;
    try {
      await axios.post(`${API}/api/admin/portfolios/${p.id}/archive`, {}, { headers });
      toast.success("Portfolio archived");
      load();
    } catch {
      toast.error("Archive failed");
    }
  };

  const openSchools = async (p) => {
    const r = await axios.get(`${API}/api/admin/portfolios/${p.id}`, { headers });
    setSchoolsModal(r.data);
  };
  const openOwners = async (p) => {
    const r = await axios.get(`${API}/api/admin/portfolios/${p.id}`, { headers });
    setOwnersModal(r.data);
  };
  const refreshModal = async (kind, id) => {
    const r = await axios.get(`${API}/api/admin/portfolios/${id}`, { headers });
    if (kind === "schools") setSchoolsModal(r.data); else setOwnersModal(r.data);
    load();
  };

  return (
    <div data-testid="portfolios-admin-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900"><Briefcase className="h-5 w-5 text-[var(--brand)]" /><h1 className="text-2xl font-semibold">School Portfolios</h1></div>
          <p className="mt-1 text-sm text-slate-500">Group schools and assign Portfolio Owners for cross-school oversight.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36" data-testid="portfolio-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="portfolio-create-btn" onClick={openCreate}><Plus className="mr-1 h-4 w-4" /> New Portfolio</Button>
          <Button variant="outline" data-testid="benchmark-settings-btn" onClick={openBench}><Settings className="mr-1 h-4 w-4" /> Benchmark Settings</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : !portfolios.length ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">No portfolios yet. Create your first one.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {portfolios.map((p) => (
            <div key={p.id} data-testid={`portfolio-row-${p.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{p.name}</h3>
                  <p className="text-xs text-slate-400">{TYPE_LABELS[p.type] || p.type}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[p.status] || ""}`}>{p.status}</span>
              </div>
              {p.description && <p className="mt-2 line-clamp-2 text-sm text-slate-500">{p.description}</p>}
              <div className="mt-3 flex gap-4 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1"><Building2 className="h-4 w-4 text-slate-400" /> {p.school_count} schools</span>
                <span className="inline-flex items-center gap-1"><Users className="h-4 w-4 text-slate-400" /> {p.owner_count} owners</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" data-testid={`portfolio-preview-btn-${p.id}`} onClick={() => window.open(`/portfolio/dashboard?pid=${p.id}`, "_blank")}><Eye className="mr-1 h-3.5 w-3.5" /> Preview Dashboard</Button>
                <Button size="sm" variant="outline" data-testid={`portfolio-assign-schools-btn-${p.id}`} onClick={() => openSchools(p)}><Building2 className="mr-1 h-3.5 w-3.5" /> Schools</Button>
                <Button size="sm" variant="outline" data-testid={`portfolio-assign-owners-btn-${p.id}`} onClick={() => openOwners(p)}><Users className="mr-1 h-3.5 w-3.5" /> Owners</Button>
                <Button size="sm" variant="outline" data-testid={`portfolio-edit-btn-${p.id}`} onClick={() => openEdit(p)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                {p.status !== "archived" && <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" data-testid={`portfolio-archive-btn-${p.id}`} onClick={() => archive(p)}><Archive className="mr-1 h-3.5 w-3.5" /> Archive</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="portfolio-create-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Portfolio" : "New Portfolio"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input data-testid="portfolio-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="NJ Authorizer Portfolio" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="portfolio-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editing && (
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="portfolio-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-xs">Description</Label><Textarea data-testid="portfolio-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div><Label className="text-xs">Internal notes (admin only)</Label><Textarea data-testid="portfolio-internal-notes-input" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="portfolio-save-btn" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {schoolsModal && (
        <AssignSchoolsModal open={!!schoolsModal} portfolio={schoolsModal} headers={headers}
          onClose={() => setSchoolsModal(null)} onChanged={() => refreshModal("schools", schoolsModal.id)} />
      )}
      {ownersModal && (
        <AssignOwnersModal open={!!ownersModal} portfolio={ownersModal} headers={headers}
          onClose={() => setOwnersModal(null)} onChanged={() => refreshModal("owners", ownersModal.id)} />
      )}

      <Dialog open={benchOpen} onOpenChange={setBenchOpen}>
        <DialogContent data-testid="benchmark-settings-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><GitCompare className="h-4 w-4" /> Benchmark Analysis Settings</DialogTitle></DialogHeader>
          {bench ? (
            <div className="space-y-4">
              <label className="flex items-center justify-between gap-3 text-sm">
                <div><div className="font-medium text-slate-800">Enable Benchmark Improvement Analysis</div><div className="text-xs text-slate-500">Portfolio Owners can compare schools.</div></div>
                <Checkbox data-testid="bench-enabled" checked={bench.enabled} onCheckedChange={(v) => saveBench({ enabled: !!v })} />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                <div><div className="font-medium text-slate-800">Enable AI Benchmark Recommendations</div><div className="text-xs text-slate-500">AI-generated action narratives (rolls out next phase).</div></div>
                <Checkbox data-testid="bench-ai" checked={bench.ai_enabled} onCheckedChange={(v) => saveBench({ ai_enabled: !!v })} />
              </label>
              <label className="flex items-center justify-between gap-3 text-sm">
                <div><div className="font-medium text-slate-800">Allow Export</div><div className="text-xs text-slate-500">CSV export of saved analyses.</div></div>
                <Checkbox data-testid="bench-export" checked={bench.allow_export} onCheckedChange={(v) => saveBench({ allow_export: !!v })} />
              </label>
              <div className="text-sm">
                <div className="font-medium text-slate-800 mb-1">Default Analysis Scope</div>
                <Select value={bench.default_scope} onValueChange={(v) => saveBench({ default_scope: v })}>
                  <SelectTrigger data-testid="bench-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="management_controllable">Management-Controllable Areas</SelectItem>
                    <SelectItem value="all_pillars">All Diagnostic Pillars</SelectItem>
                    <SelectItem value="governance_compliance">Governance and Compliance Only</SelectItem>
                    <SelectItem value="leadership_fiscal">Leadership and Fiscal Only</SelectItem>
                    <SelectItem value="instructional_systems">Instructional Systems Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-slate-400">Maximum schools per comparison: 3 (fixed). Data-safety controls always enabled.</div>
            </div>
          ) : <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
